import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { PanelLeft } from 'lucide-react'
import type { ResolvedDocument } from '@/types/blocks'
import './document-header.css'

interface DocumentHeaderProps {
	document: ResolvedDocument
	showPanel: boolean
	onTogglePanel: () => void
	onUpdateName: (name: string) => void
}

export function DocumentHeader({
	document,
	showPanel,
	onTogglePanel,
	onUpdateName,
}: DocumentHeaderProps) {
	const navigate = useNavigate()
	const [isEditingName, setIsEditingName] = useState(false)
	const [editedName, setEditedName] = useState(document.document.properties.name)

	const handleBack = () => {
		navigate('/content')
	}

	const handleNameClick = () => {
		setIsEditingName(true)
		setEditedName(document.document.properties.name)
	}

	const handleNameSave = () => {
		setIsEditingName(false)
		if (editedName.trim() !== document.document.properties.name) {
			onUpdateName(editedName.trim())
		}
	}

	const handleNameKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleNameSave()
		} else if (e.key === 'Escape') {
			setIsEditingName(false)
			setEditedName(document.document.properties.name)
		}
	}

	// Determine document size label
	const canvasPreset = document.document.properties.canvasPreset
	const sizeLabel = canvasPreset === 'letter' ? 'Letter (8.5" × 11")' :
	                  canvasPreset === 'legal' ? 'Legal (8.5" × 14")' :
	                  canvasPreset === 'tabloid' ? 'Tabloid (11" × 17")' :
	                  canvasPreset === 'a4' ? 'A4 (210mm × 297mm)' :
	                  'Custom'

	return (
		<div className="document-header">
			<div className="document-header__content">
				<div className="document-header__title-area">
					{isEditingName ? (
						<input
							className="document-header__title-input"
							type="text"
							value={editedName}
							onChange={(e) => setEditedName(e.target.value)}
							onBlur={handleNameSave}
							onKeyDown={handleNameKeyDown}
							autoFocus
						/>
					) : (
						<h1 className="document-header__title" onClick={handleNameClick}>
							{document.document.properties.name || 'Untitled Document'}
						</h1>
					)}
					<span className="document-header__size">{sizeLabel}</span>
				</div>

				<div className="document-header__actions">
					<Button
						variant="ghost"
						size="sm"
						onClick={onTogglePanel}
						className="document-header__panel-toggle"
					>
						<PanelLeft size={16} />
						{showPanel ? 'Hide' : 'Show'} Library
					</Button>
					<Button variant="action" size="sm" onClick={handleBack}>
						Back to Content
					</Button>
				</div>
			</div>
		</div>
	)
}
